import { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  ClockCounterClockwise,
  CaretLeft,
  CaretRight,
  MagnifyingGlass,
  Check,
  X,
  Clock,
  MapPin,
  User,
  Users,
  CalendarDots,
  DownloadSimple,
  Lightning,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

interface TeamAttendanceRecord {
  id: string;
  user_id: string;
  date: string;
  check_in: string;
  check_out?: string;
  status: string;
  location?: string;
  break_time?: number;
  geofence_zone_id?: string;
  is_auto_checked_in?: boolean;
  is_auto_checked_out?: boolean;
  employee_name: string;
  employee_email: string;
  employee_department: string;
  employee_avatar?: string;
}

interface TeamMember {
  id: string;
  name: string;
  department: string;
  avatar?: string;
}

interface User {
  id: string;
  name: string;
  role: string;
  department: string;
}

interface TeamAttendanceSectionProps {
  currentUser: User | null;
  teamMembers: TeamMember[];
}

export function TeamAttendanceSection({ currentUser, teamMembers }: TeamAttendanceSectionProps) {
  const [teamAttendance, setTeamAttendance] = useState<TeamAttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'day' | 'week' | 'month'>('day');

  // Get unique departments from team members
  const departments = useMemo(() => {
    const depts = new Set<string>();
    teamMembers.forEach(m => depts.add(m.department));
    return Array.from(depts).sort();
  }, [teamMembers]);

  // Format date for API
  const formatDateForAPI = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  // Fetch team attendance data
  const fetchTeamAttendance = useCallback(async () => {
    try {
      setLoading(true);
      
      const startDate = new Date(selectedDate);
      let endDate = new Date(selectedDate);
      
      if (viewMode === 'week') {
        startDate.setDate(startDate.getDate() - startDate.getDay());
        endDate.setDate(startDate.getDate() + 6);
      } else if (viewMode === 'month') {
        startDate.setDate(1);
        endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
      }

      const data = await api.getTeamAttendance({
        department: currentUser?.role === 'manager' ? currentUser.department : departmentFilter,
        startDate: formatDateForAPI(startDate),
        endDate: formatDateForAPI(endDate),
      });
      
      setTeamAttendance(data || []);
    } catch (error) {
      console.error('Error fetching team attendance:', error);
      toast.error('Failed to load team attendance');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, viewMode, currentUser?.role, currentUser?.department, departmentFilter]);

  useEffect(() => {
    fetchTeamAttendance();
  }, [fetchTeamAttendance]);

  // Filter attendance records
  const filteredAttendance = useMemo(() => {
    return teamAttendance.filter(record => {
      // Status filter
      if (statusFilter !== 'all' && record.status !== statusFilter) return false;
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = record.employee_name?.toLowerCase().includes(query);
        const matchesEmail = record.employee_email?.toLowerCase().includes(query);
        const matchesDept = record.employee_department?.toLowerCase().includes(query);
        if (!matchesName && !matchesEmail && !matchesDept) return false;
      }
      
      return true;
    });
  }, [teamAttendance, statusFilter, searchQuery]);

  // Group by date for summary
  const attendanceByDate = useMemo(() => {
    const grouped: Record<string, TeamAttendanceRecord[]> = {};
    filteredAttendance.forEach(record => {
      if (!grouped[record.date]) {
        grouped[record.date] = [];
      }
      grouped[record.date].push(record);
    });
    return grouped;
  }, [filteredAttendance]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalMembers = currentUser?.role === 'manager' 
      ? teamMembers.filter(m => m.department === currentUser.department).length
      : departmentFilter !== 'all'
      ? teamMembers.filter(m => m.department === departmentFilter).length
      : teamMembers.length;
    
    const todayStr = formatDateForAPI(new Date());
    const todayRecords = teamAttendance.filter(r => r.date === todayStr);
    const presentToday = todayRecords.filter(r => r.status === 'present' || r.status === 'late').length;
    const lateToday = todayRecords.filter(r => r.status === 'late').length;
    const absentToday = totalMembers - presentToday;
    
    return {
      totalMembers,
      presentToday,
      lateToday,
      absentToday,
    };
  }, [teamAttendance, teamMembers, currentUser, departmentFilter]);

  // Navigate dates
  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    } else {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    }
    setSelectedDate(newDate);
  };

  // Format time for display
  const formatTime = (isoString?: string) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Calculate work duration
  const calculateDuration = (checkIn: string, checkOut?: string) => {
    if (!checkOut) return 'In Progress';
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const hours = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60));
    const minutes = Math.floor(((end.getTime() - start.getTime()) % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  // Get status badge variant
  const getStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'present': return 'default';
      case 'late': return 'secondary';
      case 'absent': return 'destructive';
      case 'half-day': return 'outline';
      default: return 'outline';
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Date', 'Employee', 'Department', 'Check In', 'Check Out', 'Duration', 'Status', 'Location'];
    const rows = filteredAttendance.map(record => [
      record.date,
      record.employee_name,
      record.employee_department,
      formatTime(record.check_in),
      formatTime(record.check_out),
      calculateDuration(record.check_in, record.check_out),
      record.status,
      record.location || '-',
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `team-attendance-${formatDateForAPI(selectedDate)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Attendance exported successfully');
  };

  return (
    <div className="space-y-6" role="tabpanel" id="team-attendance-panel" aria-labelledby="team-attendance-tab">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ClockCounterClockwise className="w-8 h-8" />
            Team Attendance
          </h1>
          <p className="text-muted-foreground">Monitor and track your team's attendance records</p>
        </div>
        <Button variant="outline" onClick={exportToCSV}>
          <DownloadSimple className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Team Size</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              {stats.totalMembers}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Present Today</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600 flex items-center gap-2">
              <Check className="w-5 h-5" />
              {stats.presentToday}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Late Today</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              {stats.lateToday}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Absent Today</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600 flex items-center gap-2">
              <X className="w-5 h-5" />
              {stats.absentToday}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            {/* Date Navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigateDate('prev')}
                aria-label="Previous"
              >
                <CaretLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                onClick={() => setSelectedDate(new Date())}
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigateDate('next')}
                aria-label="Next"
              >
                <CaretRight className="w-4 h-4" />
              </Button>
              <div className="text-lg font-semibold ml-2">
                {viewMode === 'day' && selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                {viewMode === 'week' && `Week of ${selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                {viewMode === 'month' && selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </div>
            </div>
            {/* View Mode */}
            <div className="flex items-center gap-2">
              <select
                className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value as 'day' | 'week' | 'month')}
              >
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4 pb-4 border-b">
            {/* Department Filter - Only for HR/Admin/Superadmin */}
            {currentUser?.role !== 'manager' && (
              <select
                className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
              >
                <option value="all">All Departments</option>
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            )}
            
            {/* Status Filter */}
            <select
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="present">Present</option>
              <option value="late">Late</option>
              <option value="absent">Absent</option>
              <option value="half-day">Half Day</option>
            </select>

            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or department..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
          </div>

          {/* Attendance List */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-muted-foreground">Loading team attendance...</p>
            </div>
          ) : filteredAttendance.length === 0 ? (
            <div className="text-center py-8">
              <CalendarDots className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">No attendance records found for this period</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(attendanceByDate)
                .sort((a, b) => b[0].localeCompare(a[0]))
                .map(([date, records]) => (
                  <div key={date}>
                    <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                      {new Date(date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
                      <span className="ml-2 text-xs">({records.length} records)</span>
                    </h3>
                    <div className="space-y-2">
                      {records.map((record) => (
                        <div
                          key={record.id}
                          className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-lg">
                              {record.employee_avatar || record.employee_name?.charAt(0) || <User className="w-5 h-5" />}
                            </div>
                            <div>
                              <div className="font-medium">{record.employee_name}</div>
                              <div className="text-sm text-muted-foreground">{record.employee_department}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-6 text-sm">
                            <div className="text-center">
                              <div className="text-muted-foreground text-xs">Check In</div>
                              <div className="font-medium text-green-600 flex items-center gap-1 justify-center">
                                {formatTime(record.check_in)}
                                {record.is_auto_checked_in && (
                                  <Lightning className="h-3 w-3 text-blue-500" weight="fill" aria-label="Auto check-in" />
                                )}
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-muted-foreground text-xs">Check Out</div>
                              <div className="font-medium text-red-600 flex items-center gap-1 justify-center">
                                {formatTime(record.check_out)}
                                {record.is_auto_checked_out && (
                                  <Lightning className="h-3 w-3 text-orange-500" weight="fill" aria-label="Auto check-out" />
                                )}
                              </div>
                            </div>
                            <div className="text-center min-w-[80px]">
                              <div className="text-muted-foreground text-xs">Duration</div>
                              <div className="font-medium">{calculateDuration(record.check_in, record.check_out)}</div>
                            </div>
                            {record.location && (
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <MapPin className="w-4 h-4" />
                                {record.location}
                              </div>
                            )}
                            <Badge variant={getStatusVariant(record.status)}>
                              {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
